import { useId } from 'react';
import { Text, VisuallyHidden, tokens } from '@/design';
import { PolygonSymbolPicker } from './PolygonSymbolPicker';
import { LineSymbolPicker } from './LineSymbolPicker';
import type { IdentifyGeometryKind } from './vectorIdentifyModel';
import {
  buildUniqueValueClasses,
  uniqueValueKey,
  type UniqueValueClass,
  type UniqueValueColorScheme,
  type VectorStyleState,
} from './vectorStyleModel';

export interface UniqueValueSymbologyPanelProps {
  readonly state: VectorStyleState;
  readonly availableFields: ReadonlyArray<string>;
  readonly features: ReadonlyArray<{
    readonly properties?: Readonly<Record<string, unknown>>;
  }>;
  readonly readOnly: boolean;
  readonly geometryKind?: IdentifyGeometryKind;
  readonly onChange: (next: VectorStyleState) => void;
  readonly 'data-testid'?: string;
}

const SCHEMES: ReadonlyArray<{ id: UniqueValueColorScheme; label: string }> = [
  { id: 'vivid', label: 'Vivid' },
  { id: 'pastel', label: 'Pastel' },
  { id: 'earth', label: 'Earth' },
];

const TABLE_MIN_WIDTH = 560;

export function UniqueValueSymbologyPanel({
  state,
  availableFields,
  features,
  readOnly,
  geometryKind = 'polygon',
  onChange,
  'data-testid': testId = 'unique-value-symbology',
}: UniqueValueSymbologyPanelProps) {
  const fieldId = useId();
  const schemeId = useId();

  const updateClasses = (classes: ReadonlyArray<UniqueValueClass>) =>
    onChange({ ...state, uniqueValueClasses: classes });

  const updateClass = (id: string, patch: Partial<UniqueValueClass>) =>
    updateClasses(
      state.uniqueValueClasses.map((entry) =>
        entry.id === id ? { ...entry, ...patch } : entry,
      ),
    );

  return (
    <section
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3], minWidth: 0 }}
    >
      <label htmlFor={fieldId} style={labelStyle()}>
        Field
      </label>
      <select
        id={fieldId}
        value={state.uniqueValueField ?? ''}
        disabled={readOnly}
        onChange={(event) => {
          const field = event.target.value || null;
          onChange({
            ...state,
            uniqueValueField: field,
            uniqueValueClasses: field
              ? buildUniqueValueClasses(features, field, state.uniqueValueScheme)
              : [],
          });
        }}
        data-testid={`${testId}-field`}
        style={selectStyle()}
      >
        <option value="">— Choose attribute —</option>
        {availableFields.map((field) => (
          <option key={field} value={field}>{field}</option>
        ))}
      </select>

      <label htmlFor={schemeId} style={labelStyle()}>
        Color scheme
      </label>
      <select
        id={schemeId}
        value={state.uniqueValueScheme}
        disabled={readOnly || !state.uniqueValueField}
        onChange={(event) => {
          const scheme = event.target.value as UniqueValueColorScheme;
          onChange({
            ...state,
            uniqueValueScheme: scheme,
            uniqueValueClasses: state.uniqueValueField
              ? buildUniqueValueClasses(features, state.uniqueValueField, scheme)
              : [],
          });
        }}
        data-testid={`${testId}-scheme`}
        style={selectStyle()}
      >
        {SCHEMES.map((scheme) => (
          <option key={scheme.id} value={scheme.id}>{scheme.label}</option>
        ))}
      </select>

      {state.uniqueValueField ? (
        <>
          <Text variant="caption" as="span">
            {state.uniqueValueClasses.length} unique classes
          </Text>
          <div
            style={{
              minWidth: 0,
              maxHeight: tokens.size.dropdownMaxHeight,
              overflowX: 'auto',
              overflowY: 'auto',
              border: tokens.border.thin,
              borderRadius: tokens.radius.sm,
            }}
          >
            <table
              aria-label="Unique value classes"
              data-testid={`${testId}-table`}
              style={{
                width: '100%',
                minWidth: TABLE_MIN_WIDTH,
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
              }}
            >
              <thead>
                <tr>
                  <th scope="col" style={{ ...headerCellStyle(), width: tokens.density.touchMin }}>
                    <VisuallyHidden>Visible</VisuallyHidden>
                  </th>
                  <th scope="col" style={{ ...headerCellStyle(), width: 72 }}>Symbol</th>
                  <th scope="col" style={headerCellStyle()}>Value</th>
                  <th scope="col" style={headerCellStyle()}>Label</th>
                  <th scope="col" style={{ ...headerCellStyle(), width: 56 }}>
                    {geometryKind === 'line' ? 'Color' : 'Fill'}
                  </th>
                  {geometryKind === 'line' ? null : (
                    <th scope="col" style={{ ...headerCellStyle(), width: 56 }}>Outline</th>
                  )}
                  <th scope="col" style={{ ...headerCellStyle(), width: 88 }}>Opacity</th>
                  <th scope="col" style={{ ...headerCellStyle(), width: 56 }}>Width</th>
                </tr>
              </thead>
              <tbody>
                {state.uniqueValueClasses.map((entry, index) => {
                  const valueText = entry.value == null ? '<Null>' : String(entry.value);
                  return (
                    <tr
                      key={entry.id}
                      style={{
                        background: index % 2 === 0
                          ? tokens.color.surface[1]
                          : tokens.color.surface[2],
                      }}
                    >
                      <td style={cellStyle()}>
                        <input
                          type="checkbox"
                          checked={entry.visible}
                          disabled={readOnly}
                          aria-label={`Show ${entry.label}`}
                          onChange={(event) =>
                            updateClass(entry.id, { visible: event.target.checked })
                          }
                        />
                      </td>
                      <td style={{ ...cellStyle(), overflow: 'visible' }}>
                        {geometryKind === 'line' ? (
                          <LineSymbolPicker
                            compact
                            value={entry.lineSymbol}
                            readOnly={readOnly}
                            onChange={(lineSymbol) =>
                              updateClass(entry.id, {
                                lineSymbol,
                                strokeColor: lineSymbol.color,
                                strokeWidth: lineSymbol.width,
                              })
                            }
                            data-testid={`${testId}-symbol-${entry.id}`}
                          />
                        ) : (
                          <PolygonSymbolPicker
                            compact
                            value={entry.polygonSymbol}
                            fillOpacity={entry.visible ? entry.fillOpacity : 0}
                            strokeColor={entry.strokeColor}
                            strokeWidth={entry.strokeWidth}
                            readOnly={readOnly}
                            onChange={(polygonSymbol) =>
                              updateClass(entry.id, {
                                polygonSymbol,
                                fillColor: polygonSymbol.backgroundColor,
                              })
                            }
                            data-testid={`${testId}-symbol-${entry.id}`}
                          />
                        )}
                      </td>
                      <td
                        title={valueText}
                        style={ellipsisCellStyle()}
                      >
                        <span data-testid={`${testId}-value-${uniqueValueKey(entry.value)}`}>
                          {valueText}
                        </span>
                      </td>
                      <td title={entry.label} style={ellipsisCellStyle()}>
                        {entry.label}
                      </td>
                      <td style={cellStyle()}>
                        <input
                          type="color"
                          value={toHex(geometryKind === 'line' ? entry.strokeColor : entry.fillColor)}
                          disabled={readOnly}
                          aria-label={`${geometryKind === 'line' ? 'Line' : 'Fill'} color for ${entry.label}`}
                          onChange={(event) =>
                            updateClass(
                              entry.id,
                              geometryKind === 'line'
                                ? {
                                    strokeColor: event.target.value,
                                    lineSymbol: { ...entry.lineSymbol, color: event.target.value },
                                  }
                                : {
                                    fillColor: event.target.value,
                                    polygonSymbol: {
                                      ...entry.polygonSymbol,
                                      backgroundColor: event.target.value,
                                    },
                                  },
                            )
                          }
                          style={colorInputStyle()}
                        />
                      </td>
                      {geometryKind === 'line' ? null : <td style={cellStyle()}>
                        <input
                          type="color"
                          value={toHex(entry.strokeColor)}
                          disabled={readOnly}
                          aria-label={`Outline color for ${entry.label}`}
                          onChange={(event) =>
                            updateClass(entry.id, { strokeColor: event.target.value })
                          }
                          style={colorInputStyle()}
                        />
                      </td>}
                      <td style={cellStyle()}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={geometryKind === 'line' ? entry.lineSymbol.opacity : entry.fillOpacity}
                          disabled={readOnly}
                          aria-label={`Fill opacity for ${entry.label}`}
                          onChange={(event) =>
                            updateClass(
                              entry.id,
                              geometryKind === 'line'
                                ? {
                                    lineSymbol: {
                                      ...entry.lineSymbol,
                                      opacity: Number(event.target.value),
                                    },
                                  }
                                : { fillOpacity: Number(event.target.value) },
                            )
                          }
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={cellStyle()}>
                        <input
                          type="number"
                          min="0"
                          max="64"
                          value={entry.strokeWidth}
                          disabled={readOnly}
                          aria-label={`Outline width for ${entry.label}`}
                          onChange={(event) =>
                            updateClass(entry.id, {
                              strokeWidth: Number(event.target.value),
                              ...(geometryKind === 'line'
                                ? {
                                    lineSymbol: {
                                      ...entry.lineSymbol,
                                      width: Number(event.target.value),
                                    },
                                  }
                                : {}),
                            })
                          }
                          style={numberInputStyle()}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <Text variant="caption" as="span">
          Choose a field to create one editable symbol per unique value.
        </Text>
      )}
    </section>
  );
}

function toHex(color: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? color : tokens.color.accent.primary;
}

function labelStyle() {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[1],
    color: tokens.color.ink.secondary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function selectStyle() {
  return {
    minHeight: tokens.density.touchMin,
    paddingInline: tokens.space[2],
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function headerCellStyle() {
  return {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: tokens.space[1],
    borderBottom: tokens.border.thin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.secondary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.semibold,
    textAlign: 'left' as const,
  } as const;
}

function cellStyle() {
  return {
    padding: tokens.space[1],
    borderBottom: tokens.border.thin,
    verticalAlign: 'middle' as const,
  };
}

function ellipsisCellStyle() {
  return {
    ...cellStyle(),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontFamily: tokens.font.family.mono,
    fontSize: tokens.font.size.sm,
  };
}

function colorInputStyle() {
  return {
    width: tokens.density.rowDense,
    height: tokens.density.rowDense,
    padding: 0,
    border: tokens.border.thin,
    background: 'transparent',
    cursor: 'pointer',
  } as const;
}

function numberInputStyle() {
  return {
    width: '100%',
    minHeight: tokens.density.rowDense,
    paddingInline: tokens.space[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[1],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
